/**
 * Vulnerability Actions Component
 *
 * Action bar for bulk vulnerability operations including:
 * - Annotate (Note, Tag, Remove Tag)
 * - Scan (Rescan Affected Assets, Confirm Affiliation)
 * - Export (Always-enabled export button)
 * - Status/Severity changes (via standard action pattern)
 *
 * Layout uses justify-between to separate selection-dependent actions (left)
 * from always-enabled export (right).
 *
 * @module sections/vulnerabilities/components/actions
 */

import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/solid';
import { useMemo } from 'react';

import { VulnerabilityManagedDropdown } from '@/components/actions/VulnerabilityManagedDropdown';
import { Dropdown } from '@/components/Dropdown';
import {
  buildAnnotateAction,
  buildExportAction,
  buildGenericCustomAction,
  buildImportAction,
  buildScanActionHandlers,
  buildShareAction,
  TableActionsBar,
} from '@/components/table/actions';
import { DropdownOption } from '@/components/ui/Dropdown';
import { useVulnerabilityActionHandlers } from '@/sections/vulnerabilities/hooks/actions';
import { useAuth } from '@/state/auth';
import { Risk } from '@/types';

/**
 * Props for VulnerabilityActions component
 */
export interface VulnerabilityActionsProps {
  /** Vulnerabilities to perform actions on */
  vulnerabilities: Risk[];
  /** Additional CSS classes */
  className?: string;
  /** Hide the "Actions:" label */
  hideLabel?: boolean;
  /** Handler for opening tag modal */
  onTagClick?: () => void;
  /** Handler for opening note modal */
  onNoteClick?: () => void;
  /** Handler for opening remove tag modal */
  onRemoveTagClick?: () => void;
  /** Handler for opening import modal */
  onImportClick: () => void;
  /** Handler for opening export modal */
  onExportClick: () => void;
  /** Handler for opening auxiliary info modal */
  onAuxInfoClick?: () => void;
  /** Additional actions for "Ticketing" dropdown (Jira ticket actions) */
  moreActions?: DropdownOption[];
  /** PlexTrac-specific actions dropdown (Praetorian users only) */
  plexTracActions?: DropdownOption[];
  /** Handler for risk status update */
  onUpdateRisk?: (risk: Risk) => Promise<void>;
  /** Handler for clearing selection after actions */
  onClearSelection?: () => void;
  /** Handler called after scan actions complete */
  onScanComplete?: () => void;
  /** Severity filter options for dropdown */
  severityOptions?: Array<{
    label: string;
    value: string;
    id: string;
    severity?: string;
  }>;
  /** Function to get risk status label */
  getRiskStatusLabel?: (status: string) => { status: string; severity: string };
  /** Hide all action buttons */
  hideActions?: boolean;
  /** Disable actions when no items selected */
  disabled?: boolean;
}

/**
 * VulnerabilityActions Component
 *
 * Renders action dropdown buttons for selected vulnerabilities.
 * Provides consistent action interface across the vulnerabilities section.
 *
 * @param props - Component props
 * @returns Action bar JSX
 *
 * @example
 * ```tsx
 * <VulnerabilityActions
 *   vulnerabilities={selectedRisks}
 *   onNoteClick={() => setShowNoteModal(true)}
 *   onTagClick={() => setShowTagModal(true)}
 *   onRemoveTagClick={() => setShowRemoveTagModal(true)}
 *   onExportClick={() => setShowExportModal(true)}
 *   onUpdateRisk={handleUpdateRisk}
 *   onClearSelection={clearSelection}
 *   severityOptions={severityFilters}
 *   getRiskStatusLabel={getRiskStatusLabel}
 * />
 * ```
 */
export function VulnerabilityActions({
  vulnerabilities,
  className,
  hideLabel,
  onNoteClick,
  onTagClick,
  onRemoveTagClick,
  onImportClick,
  onExportClick,
  onAuxInfoClick,
  moreActions,
  plexTracActions,
  onUpdateRisk,
  onClearSelection,
  onScanComplete,
  severityOptions = [],
  getRiskStatusLabel,
  hideActions,
  disabled = false,
}: VulnerabilityActionsProps) {
  const handlers = useVulnerabilityActionHandlers(
    vulnerabilities,
    onScanComplete
  );
  const { isPraetorianUser } = useAuth();

  // Disable affiliation for non-Praetorian users
  const disableAffiliation = !isPraetorianUser;

  // Severity change handler with proper typing
  const handleSeverityChange = useMemo(
    () =>
      (option: {
        value?: string | number;
        label: string;
        severity?: string;
      }) => {
        if (!onUpdateRisk || !getRiskStatusLabel || !option?.value) {
          return;
        }
        vulnerabilities.forEach(risk => {
          const currentStatus = getRiskStatusLabel(risk.status).status;
          const newSeverity = String(option.value);
          const substatus = risk.status.slice(2); // Preserve any substatus
          const combinedStatus = `${currentStatus}${newSeverity}${substatus}`;
          onUpdateRisk({
            ...risk,
            status: combinedStatus,
          });
        });
        onClearSelection?.();
      },
    [vulnerabilities, onUpdateRisk, getRiskStatusLabel, onClearSelection]
  );

  // Build action configurations
  const actions = [
    buildAnnotateAction({
      onCommentClick: onNoteClick || (() => {}),
      onTagClick: onTagClick || (() => {}),
      onRemoveTagClick: onRemoveTagClick || (() => {}),
      labels: { comment: 'Add Note' },
      testId: 'vulnerability-annotate-dropdown',
    }),

    // Manage dropdown combining Status, Severity, and Aux Info (after Annotate)
    // Uses type: 'Manage' for proper action ordering (after Annotate, before Scan)
    ...(onUpdateRisk || onAuxInfoClick
      ? [
          {
            type: 'Manage' as const,
            // Stub handlers satisfy ManageActionConfig type; render() overrides rendering
            handlers: {
              onFreezeClick: () => {},
              onActivateClick: () => {},
              onDeleteClick: () => {},
            },
            render: () => (
              <VulnerabilityManagedDropdown
                risks={vulnerabilities}
                onUpdateRisk={
                  onUpdateRisk
                    ? async (risk, _comment) => {
                        await onUpdateRisk(risk);
                        onClearSelection?.();
                      }
                    : undefined
                }
                getRiskStatusLabel={getRiskStatusLabel}
                severityOptions={severityOptions}
                onSeverityChange={handleSeverityChange}
                onAuxInfoClick={onAuxInfoClick}
                disabled={disabled}
                data-testid="vulnerability-manage-dropdown"
                label="Manage"
                icon={<EllipsisHorizontalCircleIcon className="size-4" />}
              />
            ),
            testId: 'vulnerability-manage-wrapper',
          },
        ]
      : []),

    buildScanActionHandlers({
      handlers: {
        onRescanClick: () => handlers.handleRescan(vulnerabilities),
        onAffiliationClick: () =>
          handlers.handleConfirmAffiliation(vulnerabilities),
      },
      disabledActions: { affiliation: disableAffiliation },
      testId: 'vulnerability-scan-dropdown',
    }),

    buildShareAction({
      variant: 'button-only',
      onShareClick: handlers.handleShare,
      testId: 'vulnerability-share-button',
    }),

    buildImportAction({
      onImportClick,
      disabled: false, // Always enabled
      testId: 'vulnerability-import-button',
    }),

    buildExportAction({
      onExportClick,
      disabled: false, // Always enabled
      testId: 'vulnerability-export-button',
    }),

    ...(plexTracActions && plexTracActions.length > 0
      ? [
          buildGenericCustomAction({
            render: () => (
              <Dropdown
                placeholder="PlexTrac"
                options={plexTracActions}
                styleType="rounded"
                size="xxs"
                className="[&_span:nth-child(3)]:hidden"
                listClassName="right-0"
                icon={<EllipsisHorizontalCircleIcon className="size-4" />}
                data-testid="vulnerability-plextrac-dropdown"
              />
            ),
            testId: 'vulnerability-plextrac-wrapper',
          }),
        ]
      : []),

    ...(moreActions && moreActions.length > 0
      ? [
          buildGenericCustomAction({
            render: () => (
              <Dropdown
                placeholder="Ticketing"
                options={moreActions}
                styleType="rounded"
                size="xxs"
                className="[&_span:nth-child(3)]:hidden"
                listClassName="right-0"
                icon={<EllipsisHorizontalCircleIcon className="size-4" />}
                data-testid="vulnerability-ticketing-dropdown"
              />
            ),
            testId: 'vulnerability-ticketing-wrapper',
          }),
        ]
      : []),
  ];

  return (
    <TableActionsBar
      selectedItems={vulnerabilities}
      actions={actions}
      disabled={disabled}
      className={className}
      hideLabel={hideLabel}
      hideActions={hideActions}
      testIdPrefix="vulnerability"
    />
  );
}
